import React from "react";
import _ from 'lodash';
import cx from 'classnames';
import { animated, useTransition } from '@react-spring/web';
import styles from './editThumbnail.module.scss';
import Slider from "../slider";
import Tooltip from "src/components/tooltip";
import Thumbnail from "../thumbnail";
import { Banner } from "src/types/common";

interface EditThumbnailProps {
  show: boolean;
  isTransition: boolean;
  icon: Banner | null;
  entityType: string;
  position?: string;
  showToolbar: boolean;
  mediaType?: string;
  handleClick: (action: string) => void;
}

const EditThumbnail: React.FC<EditThumbnailProps> = ({ show, isTransition, icon, entityType, position, showToolbar, mediaType, handleClick }) => {
  const transitions = useTransition(show && isTransition, {
    from: { transform: 'translateX(100%)' },
    enter: { transform: 'translateX(0%)' },
    leave: { transform: 'translateX(0%)' }
  });

  const content = (
    <Slider.Body>
      <div className={styles.body}>
          <Thumbnail 
            media={icon?.background} 
            className={`${styles[entityType]}`}
            crop={icon?.crop} 
            position={position}
            alt=""
            defaultImage="/images/assets/default_asset.jpg"
            />

        <EditThumbnailToolbar show={showToolbar} handleClick={handleClick} showCrop={!!icon?.background && mediaType != "video"} showPosition={entityType !== "group"}/>
      </div>
    </Slider.Body>
  );

  if (!isTransition) {
    return show ? <div>{content}</div> : null;
  }

  return transitions(
    (style, item) =>
      item ? (
        <animated.div style={style}>
          {content}
        </animated.div>
      ) : null
  );
};


export { EditThumbnail };

interface EditThumbnailToolbarProps {
  show: boolean;
  showPosition?: boolean;
  showCrop?: boolean;
  handleClick: (action: string) => void;
}

const EditThumbnailToolbar: React.FC<EditThumbnailToolbarProps> = ({ show, showPosition, showCrop = true, handleClick }) => {
  const transitions = useTransition(show, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 500 }
  });

  return transitions(
    (style, item) =>
      item ? (
        <animated.div style={style} className={cx(styles.toolbar)}>
        <Tooltip content="Edit" placement="top">
          <div>
          <IconButton icon="paperflite-pen" onClick={() => handleClick("edit")} />
          </div>
        </Tooltip>
        
          {showCrop && (
            <Tooltip content="Crop" placement="top">
              <div>
              <IconButton icon="paperflite-crop" onClick={() => handleClick("crop")} />
              </div>
            </Tooltip>
          )}
      
        {showPosition && (
          <Tooltip content="Position" placement="top">
            <div>
            <IconButton icon="paperflite-focus" onClick={() => handleClick("position")} />
            </div>
          </Tooltip>
        )}
      </animated.div>
      ) : null
  );
};

interface IconButtonProps {
  icon: string;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

const IconButton: React.FC<IconButtonProps> = ({ icon, onClick, disabled }) => {
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!disabled && onClick) {
      onClick(event);
    }
  };

  return (
    <div className={cx(styles.item, styles.iconItem, {
      [styles.disabled]: disabled,
    })} onClick={handleClick}>
      <i className={cx(styles.icon, icon)}></i>
    </div>
  );
}

export default EditThumbnailToolbar;